/**
 * TruckAnim — a charming refrigerated box-truck glyph that rides an SVG route.
 *
 * It follows a `<path>` (passed by ref) sampled with `getPointAtLength`, driven
 * by a `dist` MotionValue (current arc-length, in user units). RouteMap animates
 * `dist`; this component just places + orients the truck each frame.
 *
 * The truck art is centred on (0,0) and faces +x. We orient with the SVG
 * `transform` attribute (translate → rotate → scale) so rotation pivots on the
 * truck centre with no CSS transform-box ambiguity. When the heading points left
 * we mirror on Y (scale 1 −1); composed with the rotation that reads as a clean
 * horizontal flip, keeping the truck upright instead of upside-down.
 */

import { type MotionValue, useMotionValueEvent } from 'framer-motion'
import { useLayoutEffect, useRef } from 'react'

export interface TruckAnimProps {
  /** the route path the truck rides */
  pathRef: React.RefObject<SVGPathElement | null>
  /** current arc-length along the path (RouteMap animates this) */
  dist: MotionValue<number>
  /** art scale, default 1 */
  size?: number
  /** bumps a re-sample when the route geometry changes */
  revision?: number
}

const COLD = 'oklch(0.62 0.07 232)' // reefer cool accent (only cold note in a warm palette)

export function TruckAnim({ pathRef, dist, size = 1, revision = 0 }: TruckAnimProps) {
  const truckRef = useRef<SVGGElement | null>(null)
  const shadowRef = useRef<SVGGElement | null>(null)

  const place = (d: number) => {
    const path = pathRef.current
    const truck = truckRef.current
    if (!path || !truck) return
    const total = path.getTotalLength()
    if (!Number.isFinite(total) || total <= 0) return
    const at = Math.max(0, Math.min(d, total))
    const p = path.getPointAtLength(at)
    // heading from a short chord around the current point
    const a = path.getPointAtLength(Math.max(at - 1.5, 0))
    const b = path.getPointAtLength(Math.min(at + 1.5, total))
    const ang = (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI
    const flip = Math.abs(ang) > 90 ? -1 : 1
    const x = p.x.toFixed(2)
    const y = p.y.toFixed(2)
    truck.setAttribute(
      'transform',
      `translate(${x} ${y}) rotate(${ang.toFixed(2)}) scale(1 ${flip})`,
    )
    shadowRef.current?.setAttribute('transform', `translate(${x} ${y})`)
  }

  useMotionValueEvent(dist, 'change', place)
  // initial + on geometry change
  useLayoutEffect(() => {
    place(dist.get())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revision])

  return (
    <g>
      {/* ground shadow — stays flat under the truck (no rotate/flip) */}
      <g ref={shadowRef}>
        <ellipse
          cx={0}
          cy={13 * size}
          rx={19 * size}
          ry={3.4 * size}
          fill="oklch(0.3 0.02 60)"
          opacity={0.14}
        />
      </g>

      <g ref={truckRef}>
        <g transform={`scale(${size})`}>
          {/* reefer box */}
          <rect
            x={-19}
            y={-11.5}
            width={27}
            height={21}
            rx={3.2}
            fill="var(--card)"
            stroke="var(--amber-strong)"
            strokeWidth={1.6}
          />
          {/* cold vents */}
          <line
            x1={-15}
            y1={-6.5}
            x2={-15}
            y2={5.5}
            stroke={COLD}
            strokeWidth={1.1}
            opacity={0.5}
            strokeLinecap="round"
          />
          <line
            x1={-11.5}
            y1={-6.5}
            x2={-11.5}
            y2={5.5}
            stroke={COLD}
            strokeWidth={1.1}
            opacity={0.5}
            strokeLinecap="round"
          />
          {/* snowflake — reefer badge */}
          <g stroke={COLD} strokeWidth={1.1} strokeLinecap="round">
            <line x1={-4} y1={-4} x2={-4} y2={4} />
            <line x1={-8} y1={0} x2={0} y2={0} />
            <line x1={-6.8} y1={-2.8} x2={-1.2} y2={2.8} />
            <line x1={-6.8} y1={2.8} x2={-1.2} y2={-2.8} />
          </g>
          {/* cab */}
          <path
            d="M 8 -8.5 L 14.5 -8.5 Q 18.5 -8.5 19 -3.5 L 19.6 6 Q 19.7 9.5 16 9.5 L 8 9.5 Z"
            fill="var(--amber)"
            stroke="var(--amber-strong)"
            strokeWidth={1.4}
          />
          {/* windscreen */}
          <path
            d="M 10 -6.5 L 14.2 -6.5 Q 16.6 -6.5 17 -2.5 L 17.2 0 L 10 0 Z"
            fill="var(--ink-soft)"
            opacity={0.85}
          />
          {/* headlight */}
          <circle
            cx={18.9}
            cy={4.5}
            r={1.3}
            fill="var(--amber-soft)"
            stroke="var(--amber-strong)"
            strokeWidth={0.5}
          />
          {/* wheels */}
          <Wheel cx={-9} />
          <Wheel cx={12} />
        </g>
      </g>
    </g>
  )
}

function Wheel({ cx }: { cx: number }) {
  return (
    <g transform={`translate(${cx} 10)`}>
      <circle r={4} fill="oklch(0.28 0.015 60)" />
      <circle r={1.7} fill="oklch(0.72 0.02 70)" />
    </g>
  )
}
